"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";

/**
 * Site-wide toast notifications.
 *
 * Two ways to use it:
 *
 *   1. Imperatively, from any client component (fetch-based actions, buttons, etc.):
 *        import { toast } from "@/components/ui/toast";
 *        toast.success("Call logged");
 *        toast.error("Could not place the call.");
 *
 *   2. Declaratively, for `useActionState` server-action forms that return
 *      `{ ok?: string; error?: string }`:
 *        import { useActionToast } from "@/components/ui/toast";
 *        const [state, formAction, pending] = useActionState(action, null);
 *        useActionToast(state); // pops a toast whenever a fresh result arrives
 *
 * The store is a module-level singleton + `<Toaster />` (mounted once in the root
 * layout) subscribes to it. No context/provider wiring is required, so `toast.*`
 * can be called from anywhere without prop drilling.
 */

export type ToastVariant = "success" | "error" | "info";

export type Toast = {
  id: number;
  variant: ToastVariant;
  message: string;
  /** Auto-dismiss delay in ms. */
  duration: number;
};

// --- Store -----------------------------------------------------------------

let toasts: Toast[] = [];
const listeners = new Set<() => void>();
let nextId = 1;

// Stable reference for the server snapshot so useSyncExternalStore doesn't loop.
const EMPTY: Toast[] = [];

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Toast[] {
  return toasts;
}

function getServerSnapshot(): Toast[] {
  return EMPTY;
}

function dismiss(id: number) {
  const next = toasts.filter((t) => t.id !== id);
  if (next.length !== toasts.length) {
    toasts = next;
    emit();
  }
}

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  // Errors linger a touch longer so they're not missed.
  success: 4000,
  info: 4500,
  error: 7000,
};

function show(message: string, variant: ToastVariant, duration?: number): number {
  const text = message?.trim();
  if (!text) return 0;
  const id = nextId++;
  // Cap the stack so a burst of actions can't bury the screen; drop oldest.
  toasts = [...toasts, { id, variant, message: text, duration: duration ?? DEFAULT_DURATION[variant] }].slice(-4);
  emit();
  return id;
}

type ToastOptions = { duration?: number };

export const toast = {
  success: (message: string, opts?: ToastOptions) => show(message, "success", opts?.duration),
  error: (message: string, opts?: ToastOptions) => show(message, "error", opts?.duration),
  info: (message: string, opts?: ToastOptions) => show(message, "info", opts?.duration),
  dismiss,
};

// --- Declarative hook for useActionState forms -----------------------------

type ActionResultLike = {
  ok?: string | null;
  error?: string | null;
} | null | undefined;

/**
 * Pops a toast whenever `state` becomes a fresh result object. `useActionState`
 * returns a new object reference on every submission, so we fire on reference
 * change (and never on the initial render). Errors win over success if both are
 * somehow present.
 */
export function useActionToast(state: ActionResultLike) {
  const previous = useRef<ActionResultLike>(state);
  useEffect(() => {
    if (state && state !== previous.current) {
      if (state.error) toast.error(state.error);
      else if (state.ok) toast.success(state.ok);
    }
    previous.current = state;
  }, [state]);
}

// --- UI --------------------------------------------------------------------

const VARIANT_STYLES: Record<ToastVariant, { container: string; icon: string; glyph: string; label: string }> = {
  success: {
    container: "border-[var(--fit-high-border)] bg-[var(--fit-high-muted)] text-[var(--foreground)]",
    icon: "bg-[var(--fit-high-muted)] text-[var(--fit-high)]",
    glyph: "✓",
    label: "Success",
  },
  error: {
    container: "border-red-500/35 bg-red-500/[0.10] text-[var(--foreground)]",
    icon: "bg-red-500/15 text-red-300",
    glyph: "!",
    label: "Error",
  },
  info: {
    container: "border-[var(--border-strong)] bg-[var(--surface-2)] text-[var(--foreground)]",
    icon: "bg-[var(--accent-muted)] text-[var(--accent)]",
    glyph: "i",
    label: "Notice",
  },
};

function ToastCard({ toast: t }: { toast: Toast }) {
  const [leaving, setLeaving] = useState(false);
  const [entered, setEntered] = useState(false);
  const styles = VARIANT_STYLES[t.variant];

  useEffect(() => {
    // Trigger the enter transition on the next frame.
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setLeaving(true), t.duration);
    return () => window.clearTimeout(timer);
  }, [t.duration]);

  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(() => dismiss(t.id), 220);
    return () => window.clearTimeout(timer);
  }, [leaving, t.id]);

  return (
    <div
      role={t.variant === "error" ? "alert" : "status"}
      className={`pointer-events-auto flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-[var(--shadow-md)] backdrop-blur-sm transition-all duration-200 ease-out ${
        styles.container
      } ${entered && !leaving ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}
    >
      <span
        aria-hidden
        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${styles.icon}`}
      >
        {styles.glyph}
      </span>
      <p className="min-w-0 flex-1 leading-snug">{t.message}</p>
      <button
        type="button"
        onClick={() => setLeaving(true)}
        aria-label="Dismiss notification"
        className="-mr-1 -mt-0.5 shrink-0 rounded-md p-1 text-[var(--label)] transition-colors hover:text-[var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        <span aria-hidden className="block text-base leading-none">
          ×
        </span>
      </button>
    </div>
  );
}

/** Mount once, near the root of the app. Renders the active toast stack. */
export function Toaster() {
  const items = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  if (items.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[200] flex flex-col items-center gap-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:inset-x-auto sm:right-0 sm:max-w-sm sm:items-end sm:px-6"
    >
      {items.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
}
